import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Modal } from 'react-bootstrap';
import { BsChevronDown, BsChevronRight, BsLock, BsStar, BsStarFill, BsTrash } from 'react-icons/bs';
import Skeleton from 'react-loading-skeleton';
import AppContext from 'Utils/AppContext';
import {
  listSchemaLibrary,
  getSchemaLibraryEntry,
  deleteSchemaLibraryEntry,
} from './schemaLibraryApi';
import { INK, STATUS, RADIUS } from 'Utils/styleTokens';

// The commercial schema library: every application fingerprint (bundled + custom) the
// profile matcher recognizes. Bundled entries ship with the platform and are read-only;
// custom entries come from "Add to schema library" on a profiled data source and can be
// deactivated / deleted by admins.

const badgeStyle = (bundled) => ({
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: 10,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  background: bundled ? STATUS.info.bg : STATUS.success.bg,
  color: bundled ? STATUS.info.text : STATUS.success.text,
});

const SchemaLibraryScreen = ({ isAdmin }) => {
  const history = useHistory();
  const location = useLocation();
  const { setNotification } = useContext(AppContext);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  // entry id -> full entry (tables) once expanded; null while the fetch is in flight.
  const [expanded, setExpanded] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // ?highlight=<schemaId> — set by the identity detail's "View in Schema Library" link.
  const highlightId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const v = params.get('highlight');
    return v != null ? String(v) : null;
  }, [location.search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listSchemaLibrary();
      setEntries(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setError((e && e.message) || 'Failed to load the schema library.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) =>
      [e.vendorName, e.productName, e.category, e.productVersion]
        .some((v) => v && String(v).toLowerCase().includes(q)));
  }, [entries, query]);

  const toggleExpand = async (entry) => {
    const id = entry.id;
    if (expanded[id] !== undefined) {
      setExpanded((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return;
    }
    setExpanded((prev) => ({ ...prev, [id]: null }));
    try {
      const full = await getSchemaLibraryEntry(id);
      setExpanded((prev) => (id in prev ? { ...prev, [id]: full } : prev));
    } catch (e) {
      setExpanded((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setNotification({
        header: 'Load failed',
        body: <span>Could not load tables for {entry.productName}.</span>,
      });
    }
  };

  const handleDelete = async () => {
    const entry = confirmDelete;
    setConfirmDelete(null);
    if (!entry) return;
    setBusyId(entry.id);
    try {
      await deleteSchemaLibraryEntry(entry.id);
      setNotification({
        header: 'Fingerprint removed',
        body: <span>{entry.vendorName} {entry.productName} no longer matches future fingerprint runs.</span>,
      });
      load();
    } catch (e) {
      setNotification({
        header: 'Delete failed',
        body: <span>{(e && e.message) || 'Could not delete the entry.'}</span>,
      });
    } finally {
      setBusyId(null);
    }
  };

  // Deactivate = soft-off without losing the entry: PUT with the same metadata is not
  // needed — the worker's DELETE is already a soft-delete (is_active=false). A separate
  // "deactivate" verb therefore maps to the same call; we surface only Delete to keep
  // the surface small. (Reactivation is a PUT with the original body — admin/API-level.)

  const renderTables = (full) => {
    if (full === null) {
      return <div style={{ padding: 12 }}><Skeleton height={16} count={4} /></div>;
    }
    const tables = full.tables || [];
    return (
      <div style={{ maxHeight: 320, overflow: 'auto', borderTop: `1px solid ${INK[100]}` }}>
        <table className='metadata-itbl'>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', paddingLeft: 10 }}>Table</th>
              <th style={{ textAlign: 'left', paddingLeft: 10 }}>Fingerprint</th>
              <th style={{ textAlign: 'left', paddingLeft: 10 }}>Key table</th>
            </tr>
          </thead>
          <tbody>
            {tables.map((t) => (
              <tr key={t.originalTableName}>
                <td style={{ fontFamily: 'monospace', fontSize: 12, textAlign: 'left', paddingLeft: 10 }}>
                  {t.originalTableName}
                </td>
                <td style={{ fontFamily: 'monospace', fontSize: 12, color: INK[400], textAlign: 'left', paddingLeft: 10 }}>
                  {t.signatureHash}
                </td>
                <td style={{ textAlign: 'left', paddingLeft: 10 }}>
                  {t.keyTable
                    ? <BsStarFill size={12} style={{ color: '#b45309' }} title='Key table — drives the 70%-weighted match term' />
                    : <BsStar size={12} style={{ color: INK[200] }} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className='metadata-page'>
      <div className='metadata-toolbar'>
        <span className='mt-title'>SCHEMA LIBRARY</span>
        <span className='mt-sep'>|</span>
        <button
          type='button'
          className='metadata-sortbtn'
          onClick={() => history.push('/metadata')}
        >
          ← Back to overview
        </button>
        <input
          type='text'
          data-testid='library-search'
          placeholder='Search vendor / product / category…'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            border: `1px solid ${INK[200]}`, borderRadius: 8, fontSize: 12,
            padding: '4px 10px', marginLeft: 12, minWidth: 240, outline: 'none',
          }}
        />
        <span style={{ marginLeft: 'auto', fontSize: 12, color: INK[500] }}>
          {entries.length} fingerprint{entries.length === 1 ? '' : 's'}
        </span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#fff' }}>
        {loading && <Skeleton height={40} count={6} />}
        {error && !loading && (
          <div className='metadata-empty' style={{ color: STATUS.danger.text }}>{error}</div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div className='metadata-empty' style={{ padding: 32 }}>
            {entries.length === 0
              ? 'The schema library is empty. Bundled fingerprints load at service startup; custom ones come from "Add to schema library" on a fingerprinted data source.'
              : 'No fingerprints match the current search.'}
          </div>
        )}
        {!loading && !error && filtered.length > 0 && (
          <div style={{ border: `1px solid ${INK[150]}`, borderRadius: RADIUS.md, overflow: 'hidden' }}>
            <table className='metadata-itbl' data-testid='library-table'>
              <thead>
                <tr>
                  <th style={{ width: 28 }} />
                  <th style={{ textAlign: 'left', paddingLeft: 10 }}>Origin</th>
                  <th style={{ textAlign: 'left', paddingLeft: 10 }}>Vendor</th>
                  <th style={{ textAlign: 'left', paddingLeft: 10 }}>Product</th>
                  <th style={{ textAlign: 'left', paddingLeft: 10 }}>Version</th>
                  <th style={{ textAlign: 'left', paddingLeft: 10 }}>Category</th>
                  <th style={{ textAlign: 'left', paddingLeft: 10 }}>Sensitivity</th>
                  <th>Tables</th>
                  <th>Key</th>
                  <th>Matches</th>
                  {isAdmin && <th />}
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => {
                  const isOpen = e.id in expanded;
                  const highlighted = highlightId != null && String(e.id) === highlightId;
                  return (
                    <React.Fragment key={e.id}>
                      <tr
                        data-testid={`library-row-${e.id}`}
                        onClick={() => toggleExpand(e)}
                        style={{
                          cursor: 'pointer',
                          background: highlighted ? STATUS.info.bg : undefined,
                        }}
                      >
                        <td>{isOpen ? <BsChevronDown size={12} /> : <BsChevronRight size={12} />}</td>
                        <td style={{ textAlign: 'left', paddingLeft: 10 }}>
                          <span style={badgeStyle(e.bundled)}>
                            {e.bundled ? <><BsLock size={9} style={{ marginRight: 3 }} />Bundled</> : 'Custom'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'left', paddingLeft: 10 }}>{e.vendorName}</td>
                        <td style={{ textAlign: 'left', paddingLeft: 10, fontWeight: 600 }}>{e.productName}</td>
                        <td style={{ textAlign: 'left', paddingLeft: 10, color: INK[500] }}>{e.productVersion || '—'}</td>
                        <td style={{ textAlign: 'left', paddingLeft: 10 }}>{e.category}</td>
                        <td style={{ textAlign: 'left', paddingLeft: 10 }}>{e.defaultSensitivity}</td>
                        <td style={{ color: INK[500] }}>{e.tableCount != null ? e.tableCount : '—'}</td>
                        <td style={{ color: INK[500] }}>{e.keyTableCount != null ? e.keyTableCount : '—'}</td>
                        <td style={{ color: INK[500] }}>{e.matchCount != null ? e.matchCount : '—'}</td>
                        {isAdmin && (
                          <td onClick={(ev) => ev.stopPropagation()}>
                            {!e.bundled && (
                              <button
                                type='button'
                                data-testid={`library-delete-${e.id}`}
                                title='Remove this custom fingerprint (soft delete — match history is kept)'
                                disabled={busyId === e.id}
                                onClick={() => setConfirmDelete(e)}
                                style={{
                                  border: 'none', background: 'none', cursor: 'pointer',
                                  color: STATUS.danger.text, padding: 4,
                                }}
                              >
                                <BsTrash size={13} />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={isAdmin ? 11 : 10} style={{ padding: 0, background: INK[50] }}>
                            {e.description && (
                              <div style={{ padding: '8px 12px', fontSize: 12, color: INK[500], textAlign: 'left' }}>
                                {e.description}
                              </div>
                            )}
                            {renderTables(expanded[e.id])}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal show={!!confirmDelete} onHide={() => setConfirmDelete(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove fingerprint</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Remove <strong>{confirmDelete && `${confirmDelete.vendorName} ${confirmDelete.productName}`}</strong> from
          the schema library? Future fingerprint runs will no longer identify databases as this
          application. Existing match history is kept.
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button variant='danger' data-testid='library-delete-confirm' onClick={handleDelete}>Remove</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SchemaLibraryScreen;
